import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface DayRow {
  date: string;
  viewed_chart: number;
  read_interpretation: number;
  hit_paywall: number;
  clicked_upgrade: number;
  converted: number;
  conversion_rate_pct: number | null;
}

interface Summary {
  total_viewed:    number;
  total_converted: number;
  avg_rate:        number;
}

export function AdminConversaoFreePaidPage() {
  const [rows, setRows]       = useState<DayRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('free_to_paid_conversion')
      .select('*')
      .order('date', { ascending: false })
      .limit(30)
      .then(({ data, error: err }) => {
        if (err) { setError(err.message); setLoading(false); return; }
        const d = (data ?? []) as DayRow[];
        setRows(d);

        const totalViewed    = d.reduce((s, r) => s + (r.viewed_chart     ?? 0), 0);
        const totalConverted = d.reduce((s, r) => s + (r.converted        ?? 0), 0);
        const avgRate        = totalViewed > 0 ? (totalConverted / totalViewed) * 100 : 0;
        setSummary({ total_viewed: totalViewed, total_converted: totalConverted, avg_rate: avgRate });
        setLoading(false);
      }, () => setLoading(false));
  }, []);

  const FUNNEL_STEPS = [
    { key: 'viewed_chart',       label: 'Viram o mapa visual',      color: 'var(--petrol)' },
    { key: 'read_interpretation', label: 'Leram interpretação grátis', color: 'var(--petrol)' },
    { key: 'hit_paywall',        label: 'Bateram no paywall',         color: '#e08a00' },
    { key: 'clicked_upgrade',    label: 'Clicaram em upgrade',        color: '#c06000' },
    { key: 'converted',          label: 'Converteram (pago)',         color: '#2a7a5a' },
  ] as const;

  const totals: Record<string, number> = {};
  for (const step of FUNNEL_STEPS) {
    totals[step.key] = rows.reduce((s, r) => s + ((r as unknown as Record<string, number>)[step.key] ?? 0), 0);
  }
  const maxVal = Math.max(...Object.values(totals), 1);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando…</div>;
  }

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1000px' }}>
      <h1 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-28)', color: 'var(--bg)', marginBottom: '0.25rem' }}>
        Conversão Free → Pago
      </h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-13)', marginBottom: '2rem' }}>
        Últimos 30 dias · atualizado em tempo real
      </p>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: '#c00' }}>
          {error}
        </div>
      )}

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Viram o mapa',    value: totals['viewed_chart']       ?? 0, fmt: (n: number) => n.toLocaleString('pt-BR') },
          { label: 'Leram interp.',   value: totals['read_interpretation'] ?? 0, fmt: (n: number) => n.toLocaleString('pt-BR') },
          { label: 'Hit paywall',     value: totals['hit_paywall']         ?? 0, fmt: (n: number) => n.toLocaleString('pt-BR') },
          { label: 'Clicaram upgrade',value: totals['clicked_upgrade']     ?? 0, fmt: (n: number) => n.toLocaleString('pt-BR') },
          { label: 'Converteram',     value: totals['converted']           ?? 0, fmt: (n: number) => n.toLocaleString('pt-BR') },
          { label: 'Taxa geral',      value: summary?.avg_rate ?? 0,             fmt: (n: number) => n.toFixed(2) + '%' },
        ].map(card => (
          <div key={card.label} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius-sm)', padding: '1rem 1.25rem' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{card.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontFamily: 'var(--font-display)', fontSize: 'var(--text-22)', color: 'var(--bg)', fontWeight: 400 }}>{card.fmt(card.value)}</p>
          </div>
        ))}
      </div>

      {/* Funnel bars */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius-md)', padding: '1.5rem', marginBottom: '2rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-18)', color: 'var(--bg)', margin: '0 0 1.25rem' }}>
          Funil Free → Pago (30d)
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {FUNNEL_STEPS.map(step => {
            const val    = totals[step.key] ?? 0;
            const width  = `${Math.round((val / maxVal) * 100)}%`;
            const prev   = FUNNEL_STEPS[FUNNEL_STEPS.indexOf(step) - 1];
            const prevVal = prev ? (totals[prev.key] ?? 0) : 0;
            const drop   = prev && prevVal > 0 ? ((prevVal - val) / prevVal * 100).toFixed(1) : null;

            return (
              <div key={step.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{step.label}</span>
                  <span style={{ fontSize: 'var(--text-label)', color: 'var(--bg)', fontWeight: 600 }}>
                    {val.toLocaleString('pt-BR')}
                    {drop !== null && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>−{drop}%</span>}
                  </span>
                </div>
                <div style={{ height: '8px', background: 'var(--bg-base)', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width, background: step.color, borderRadius: '999px', transition: 'width 0.5s' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily table */}
      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-base)' }}>
              {['Data', 'Viram mapa', 'Leram', 'Hit paywall', 'Upgrade click', 'Converteram', 'Taxa'].map(h => (
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Data' ? 'left' : 'center', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', fontWeight: 600, borderBottom: '1px solid var(--line-soft)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                  Nenhum dado encontrado nos últimos 30 dias.
                </td>
              </tr>
            ) : rows.map((row, i) => (
              <tr key={row.date} style={{ borderTop: '1px solid var(--line-soft)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-surface)' }}>
                <td style={{ padding: '0.6rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{row.date}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--bg)' }}>{row.viewed_chart ?? 0}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--bg)' }}>{row.read_interpretation ?? 0}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--bg)' }}>{row.hit_paywall ?? 0}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--bg)' }}>{row.clicked_upgrade ?? 0}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600 }}>{row.converted ?? 0}</td>
                <td style={{ textAlign: 'center', padding: '0.6rem', fontSize: 'var(--text-caption)', color: row.conversion_rate_pct && row.conversion_rate_pct > 3 ? 'var(--petrol)' : 'var(--text-muted)' }}>
                  {row.conversion_rate_pct != null ? `${row.conversion_rate_pct}%` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
